import { remainingMs, timestamp, type ClockState, type Color } from '@chess/core'
import { memo, useEffect, useRef, useState, type JSX } from 'react'
import { formatClock } from '../lib/format.js'

/** Below this the clock turns red and shows tenths. */
const LOW_MS = 20_000
/** Below this it pulses, and the warning sound fires once. */
const URGENT_MS = 10_000

export interface ClockProps {
  readonly clock: ClockState
  readonly turn: Color
  readonly color: Color
  /** False once the game is over, so the display freezes. */
  readonly live: boolean
  /** serverTime - clientTime, so this agrees with the authority. */
  readonly offsetMs: number
  /** Fired once when this clock first drops under ten seconds. */
  readonly onUrgent?: () => void
}

/**
 * A self-ticking clock.
 *
 * This component owns its own timer precisely so that nothing else re-renders
 * on it. Previously the tick lived in `RoomView`, which meant all sixty-four
 * squares and every piece re-rendered ten times a second — on a mid-range phone
 * that steals frames from the very move animation you are trying to watch.
 */
const ClockImpl = ({ clock, turn, color, live, offsetMs, onUrgent }: ClockProps): JSX.Element => {
  const running = live && clock.turnStartedAt !== null && turn === color
  const [, force] = useState(0)
  const warned = useRef(false)

  useEffect(() => {
    if (!running) return
    // Ten hertz is enough for tenths and cheap now that it re-renders one node.
    const id = window.setInterval(() => force((value) => value + 1), 100)
    return () => window.clearInterval(id)
  }, [running])

  // A fresh game or a new side to move re-arms the warning.
  useEffect(() => {
    warned.current = false
  }, [clock.turnStartedAt])

  const left = remainingMs(clock, turn, color, timestamp(Date.now() + offsetMs))

  if (running && !warned.current && Number.isFinite(left) && left <= URGENT_MS) {
    warned.current = true
    onUrgent?.()
  }

  const urgent = running && Number.isFinite(left) && left <= URGENT_MS
  const low = running && Number.isFinite(left) && left <= LOW_MS

  return (
    <div
      role="timer"
      aria-label={`${color === 0 ? 'White' : 'Black'} clock`}
      className={`rounded px-3 py-1 font-mono text-2xl font-semibold tabular-nums sm:text-3xl ${
        urgent
          ? 'clock-urgent text-white'
          : low
            ? 'bg-danger text-white'
            : running
              ? 'bg-panel-raised text-ink'
              : 'bg-panel text-ink-dim'
      }`}
    >
      {formatClock(left)}
    </div>
  )
}

export const Clock = memo(ClockImpl)
